import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CbreAlertService } from '@cbre/alerts';
import { ServiceTicketService } from '@core/services/service-ticket.service';
import { Attachment, HelpTicket } from '@shared/models/help-ticket.model';
import { IUser } from '@shared/models/iuser.model';
import { GtagEventService } from '@shared/services/gtag-event.service';
import { L10nTranslationService } from 'angular-l10n';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'miq-create-service-ticket',
  templateUrl: './create-service-ticket.component.html',
  styleUrls: ['./create-service-ticket.component.scss'],
  standalone: false,
})
export class CreateServiceTicketComponent implements OnInit, AfterViewInit, OnDestroy {
  serviceTicketFormGroup: UntypedFormGroup;
  ticketInformation: HelpTicket;
  fileList: Attachment[] = [];
  formData: FormData;
  @Input() canvas: HTMLCanvasElement;
  @Input() pageId: number;
  @Input() userInfo: IUser;
  @Input() set ticket(ticketObject) {
    if (ticketObject) {
      this.ticketName.setValue(ticketObject.ticketTitle || '');
      this.description.setValue(ticketObject.ticketDescription || '');
    }
  }
  @Output() closeDialogBox: EventEmitter<any>;
  @ViewChild('fileImportInput', { static: true }) fileImportInput: any;
  @ViewChild('attachment', { static: true }) attachment: ElementRef;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private serviceTicketService: ServiceTicketService,
    private snackBar: MatSnackBar,
    public translationService: L10nTranslationService,
    private gtagEventService: GtagEventService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private cbreAlertService: CbreAlertService
  ) {
    this.serviceTicketFormGroup = this.formBuilder.group({
      ticketName: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.required]],
      screenshotAttachment: [true],
    });
    this.closeDialogBox = new EventEmitter<any>();
  }

  ngOnInit() {
    // required for static label translation
  }

  ngAfterViewInit() {
    this.attachment.nativeElement.appendChild(this.sanitizer.sanitize(SecurityContext.NONE, this.canvas));
  }

  closePopUp() {
    this.closeDialogBox.emit('close');
  }

  submitRequest(): void {
    if (this.serviceTicketFormGroup.valid) {
      this.ticketInformation = {
        pageId: this.pageId,
        createBy: `${this.userInfo.firstName} ${this.userInfo.lastName}`,
        updateBy: `${this.userInfo.firstName} ${this.userInfo.lastName}`,
        userId: this.userInfo.id,
        title: this.ticketName.value,
        desc: `${this.description.value} url:${this.router.url}`,
        attachments: [],
      };
      this.addScreenShot().subscribe(this.createTicket.bind(this));
    }
  }

  createTicket(): void {
    this.ticketInformation.attachments = this.fileList;
    this.serviceTicketService
      .createServiceTicket(this.ticketInformation)
      .pipe(take(1))
      .subscribe(
        () => {
          this.gtagEventService.onCreateGtag({
            category: 'service_ticket',
            label: this.fileList.length > 0 ? 'With Attachment' : undefined,
            value: this.pageId,
          });
          this.closeDialogBox.emit('navigate');
          this.fileList = [];
        },
        () => {
          this.snackBar.open(
            this.translationService.translate('core.ticketCreationFailed'),
            this.translationService.translate('recordsSearch.sClose'),
            {
              duration: 1500,
            }
          );
        }
      );
  }

  onFileChange(event): void {
    this.formData = new FormData();
    this.formData.append('file', event.target.files[0], event.target.files[0].name);
    if (this.fileList.filter(file => file.name === event.target.files[0].name).length === 0) {
      this.serviceTicketService.uploadFile(this.formData).subscribe(
        result => {
          result.createBy = this.userInfo.emailAddress;
          this.fileList.push(result);
        },
        () => {
          this.cbreAlertService.showErrorSnackBar_l10n('core.errorWhileUploadingFile');
        }
      );
    }
    this.fileImportInput.nativeElement.value = '';
  }

  removeFile(event): void {
    for (const file of this.fileList) {
      if (file.name === event) {
        const index = this.fileList.findIndex(fileName => fileName.name === event);
        this.fileList.splice(index, 1);
      }
    }
  }

  addScreenShot(): Observable<any> {
    if (!this.serviceTicketFormGroup.controls.screenshotAttachment.value) {
      return of([]);
    }

    const screenshotAttachment = new FormData();
    const img = this.canvas.toDataURL('image/jpeg');
    const newData = this.dataURItoBlob(img);
    screenshotAttachment.append('file', newData, `${Date.now()}${this.userInfo.id}.jpeg`);
    return this.serviceTicketService.uploadFile(screenshotAttachment).pipe(
      map(result => {
        result.createBy = this.userInfo.emailAddress;
        this.fileList.push(result);
        return result;
      })
    );
  }

  dataURItoBlob(dataURI): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: 'image/jpeg' });
  }

  get ticketName() {
    return this.serviceTicketFormGroup.get('ticketName');
  }

  get description() {
    return this.serviceTicketFormGroup.get('description');
  }

  ngOnDestroy() {
    /** label translation purpose added */
    this.fileImportInput = null;
  }
}
